import { LOCALSTORAGE_KEY } from "@config";
import Cookies from "js-cookie";

import { GTM_SIDEBAR_LINK_EVENT, GtmEvent, GtmSidebarNavigationEvent, GtmSignupMethod } from "./gtmEvents";

export type DataLayerPayload = Record<string, unknown>;

export type GtmPageViewParams = {
  page_path?: string;
  page_title?: string;
};

export type GtmPayNowParams = {
  quantity: number;
  total: number;
  credits: number;
  currency: string;
  unit_price: number;
  item_id: string;
  product_key: string;
  region: string;
  realprice: string;
};

const GTM_ENABLED_MODES = new Set(["staging", "production"]);
const AUTH_SAVED_PARAMS_KEY = "auth_saved_params";

const ATTRIBUTION_QUERY_KEYS = [
  "tc",
  "bizCode",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
] as const;

const ATTRIBUTION_FIELD_MAP: Partial<Record<(typeof ATTRIBUTION_QUERY_KEYS)[number], string>> = {
  bizCode: "biz_code",
};

/** staging / production 且浏览器环境 */
export const isGtmEnabled = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return GTM_ENABLED_MODES.has(import.meta.env.MODE);
};

/** 底层 push，非 staging/production 时 no-op */
export const pushDataLayer = (payload: DataLayerPayload): void => {
  if (!isGtmEnabled()) {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
};

const parseSavedUrlParams = (): Record<string, string> => {
  const raw = sessionStorage.getItem(AUTH_SAVED_PARAMS_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
};

const pickAttributionValue = (
  searchParams: URLSearchParams,
  savedParams: Record<string, string>,
  key: (typeof ATTRIBUTION_QUERY_KEYS)[number],
): string | undefined => {
  const value = searchParams.get(key) || savedParams[key];
  return value || undefined;
};

/** 从 URL、auth_saved_params、localStorage、Cookie 合并归因参数（有值才返回） */
export const getGtmAttributionParams = (): DataLayerPayload => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const savedParams = parseSavedUrlParams();
    const payload: DataLayerPayload = {};

    for (const key of ATTRIBUTION_QUERY_KEYS) {
      let value = pickAttributionValue(searchParams, savedParams, key);
      if (key === "tc" && !value) {
        value = localStorage.getItem(LOCALSTORAGE_KEY.TRACK_CODE) || undefined;
      }
      if (value) {
        const field = ATTRIBUTION_FIELD_MAP[key] ?? key;
        payload[field] = value;
      }
    }

    const qhclickid = Cookies.get("qhclickid");
    if (qhclickid) {
      payload.qhclickid = qhclickid;
    }

    return payload;
  } catch {
    return {};
  }
};

/** SPA 虚拟浏览 */
export const pushGtmPageView = (params?: GtmPageViewParams): void => {
  pushDataLayer({
    event: GtmEvent.PageView,
    page_path: params?.page_path ?? `${location.pathname}${location.search}`,
    page_title: params?.page_title ?? document.title,
  });
};

/** 投放自定义事件统一入口 */
export const pushGtmEvent = (event: GtmEvent, params?: DataLayerPayload): void => {
  pushDataLayer({ event, ...params });
};

export const pushGtmRegisterSuccess = (params: { signup_method: GtmSignupMethod }): void => {
  try {
    pushGtmEvent(GtmEvent.RegisterSuccess, {
      ...getGtmAttributionParams(),
      signup_method: params.signup_method,
    });
  } catch {}
};

export const pushGtmPayNow = (params: GtmPayNowParams): void => {
  pushGtmEvent(GtmEvent.PayNow, params);
};

/** 支付回调成功（/video?orderId=...）：仅上报 orderId */
export const pushGtmPurchaseCreditsSuccessfully = (orderId: string): void => {
  if (!orderId || orderId === "default") {
    return;
  }
  pushGtmEvent(GtmEvent.PurchaseCreditsSuccessfully, { orderId });
};

export type GtmSidebarNavigationParams = {
  event: GtmSidebarNavigationEvent;
  destination: string;
  from_path?: string;
};

/** 侧边栏菜单点击：统一 payload 结构 */
export const pushGtmSidebarNavigation = (params: GtmSidebarNavigationParams): void => {
  try {
    pushGtmEvent(params.event, {
      destination: params.destination,
      from_path: params.from_path ?? `${location.pathname}${location.search}`,
    });
  } catch {}
};

export const pushGtmSidebarNavigationByLink = (destination: string, from_path?: string): void => {
  const event = GTM_SIDEBAR_LINK_EVENT[destination];
  if (!event) {
    return;
  }
  pushGtmSidebarNavigation({ event, destination, from_path });
};
